import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DELIVERY_STATUS_REQUIRES_DATE } from './deliveryStatusConstants';
import DeliveryWindowSelect from './DeliveryWindowSelect';

// Shown only when moving delivery_status to an email-eligible value (ready_to_deliver,
// out_for_delivery, delivered). Defaults to checked every time the modal opens (the
// common case is to notify the customer), but the admin can still uncheck it before
// confirming to skip the send for this particular transition.
//
// For 'ready_to_deliver' ("Ready for delivery") specifically, also requires an expected
// delivery date — captured here and saved as Order.expected_delivery_date, which then
// feeds the {delivery_date} placeholder in that (and every later) confirmation email.
// The date comes with a REQUIRED time window (morning / afternoon / evening, one of three fixed choices).
export default function DeliveryStatusEmailModal({ show, orderNumber, targetStatus, onCancel, onConfirm, submitting }) {
    const { t } = useTranslation();
    const [sendEmail, setSendEmail] = useState(true);
    const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
    const [deliveryWindow, setDeliveryWindow] = useState('');
    const [dateTouched, setDateTouched] = useState(false);

    const requiresDate = DELIVERY_STATUS_REQUIRES_DATE.includes(targetStatus);

    useEffect(() => {
        if (show) {
            setSendEmail(true);
            setExpectedDeliveryDate('');
            setDeliveryWindow('');
            setDateTouched(false);
        }
    }, [show]);

    const dateMissing = requiresDate && !expectedDeliveryDate;
    const windowMissing = requiresDate && !deliveryWindow;

    const handleConfirm = () => {
        if (dateMissing || windowMissing) {
            setDateTouched(true);
            return;
        }
        onConfirm(sendEmail, requiresDate ? expectedDeliveryDate : undefined, requiresDate ? deliveryWindow : undefined);
    };

    return (
        <Modal show={show} onHide={onCancel} centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('orderDetail.deliveryStatusModal.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    {t('orderDetail.deliveryStatusModal.body', {
                        number: orderNumber,
                        status: t(`deliveryStatuses.${targetStatus}`),
                    })}
                </p>
                {requiresDate && (
                    <Form.Group className="mb-3">
                        <Form.Label>{t('orderDetail.deliveryStatusModal.expectedDeliveryDateLabel')}</Form.Label>
                        <Form.Control
                            type="date"
                            value={expectedDeliveryDate}
                            onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                            isInvalid={dateTouched && dateMissing}
                        />
                        <Form.Control.Feedback type="invalid">
                            {t('orderDetail.deliveryStatusModal.expectedDeliveryDateRequired')}
                        </Form.Control.Feedback>
                    </Form.Group>
                )}
                {requiresDate && (
                    <DeliveryWindowSelect
                        value={deliveryWindow}
                        onChange={setDeliveryWindow}
                        isInvalid={dateTouched && windowMissing}
                        idPrefix="delivery-status-window"
                    />
                )}
                <Form.Check
                    type="checkbox"
                    id="delivery-status-send-email"
                    label={t('orderDetail.deliveryStatusModal.sendEmailLabel')}
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onCancel} disabled={submitting}>
                    {t('common.cancel')}
                </Button>
                <Button variant="primary" onClick={handleConfirm} disabled={submitting}>
                    {submitting ? t('common.saving') : t('common.confirm')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
